import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart, FREE_SHIPPING_THRESHOLD, calcShippingFee } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { couponApi, pointApi } from '../../lib/supabase-api';
import { useToast } from '../ui/Toast';
import SubstitutionNotice from '../common/SubstitutionNotice';

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isLoggedIn, user } = useAuth();
  const {
    items, itemCount, subtotal, shippingFee, discount,
    updateQuantity, removeFromCart, clearCart,
    appliedCoupon, applyCoupon, removeCoupon
  } = useCart();

  const [couponCode, setCouponCode] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [availablePoints, setAvailablePoints] = useState(0);

  useEffect(() => {
    const loadPoints = async () => { if (user?.id) { const balance = await pointApi.getBalance(user.id); setAvailablePoints(balance); } };
    loadPoints();
  }, [user?.id]);

  useEffect(() => {
    const currentItemIds = items.map(item => item.id);
    const newItems = currentItemIds.filter(id => !selectedItems.includes(id));
    if (newItems.length > 0) { setSelectedItems(prev => [...prev, ...newItems]); }
    setSelectedItems(prev => prev.filter(id => currentItemIds.includes(id)));
  }, [items]);

  const handleSelectAll = (checked: boolean) => { setSelectedItems(checked ? items.map(item => item.id) : []); };
  const handleSelectItem = (itemId: string, checked: boolean) => { setSelectedItems(prev => checked ? [...prev, itemId] : prev.filter(id => id !== itemId)); };

  const handleQuantityChange = (productId: string, newQuantity: number, pillQuantity?: number) => {
    if (newQuantity < 1) removeFromCart(productId, pillQuantity); else updateQuantity(productId, newQuantity);
  };

  const handleCouponApply = async () => {
    if (!couponCode.trim()) { toast.warning('Please enter a coupon code.'); return; }
    const success = await applyCoupon(couponCode.trim());
    if (success) { toast.success('Coupon applied successfully.'); setCouponCode(''); }
  };

  const handleBestCouponApply = async () => {
    if (!user?.id) { toast.warning('Please log in first.'); return; }
    setIsProcessing(true);
    try {
      const result = await couponApi.getBestCoupon(user.id, subtotal + shippingFee);
      if (result.bestCoupon) {
        const success = await applyCoupon(result.bestCoupon.code);
        if (success) toast.success(`Best coupon applied!\n${result.bestCoupon.name} (-$${result.maxDiscount.toLocaleString()})`);
      } else { toast.info('No coupons available at this time.'); }
    } catch (error) { toast.error('An error occurred while applying the coupon.'); } finally { setIsProcessing(false); }
  };

  const handleCheckout = () => {
    if (!isLoggedIn) { toast.warning('Please log in to proceed.'); navigate('/login'); return; }
    if (selectedItems.length === 0) { toast.warning('Please select items to order.'); return; }
    navigate('/checkout', { state: { pointsToUse } });
  };

  const selectedItemsData = items.filter(item => selectedItems.includes(item.id));
  const selectedSubtotal = selectedItemsData.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const selectedShippingFee = calcShippingFee(selectedSubtotal);
  const selectedTotal = selectedSubtotal + selectedShippingFee - discount - pointsToUse;
  const freeShippingProgress = Math.min((selectedSubtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

  // Empty cart
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 bg-warm-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-10 h-10 text-warm-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0L17 18" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-forest-900 mb-2">Your cart is empty</h2>
          <p className="text-warm-500 text-sm mb-6">Looks like you haven't added any products yet.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-forest-800 text-cream-100 font-semibold text-sm rounded-lg hover:bg-forest-700 transition-all shadow-card">
            Browse Products
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100">
      <div className="section-container py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-display-sm text-forest-900">Shopping Cart</h1>
            <p className="text-sm text-warm-500 mt-0.5">{itemCount} item{itemCount !== 1 ? 's' : ''} in your cart</p>
          </div>
          <button onClick={clearCart} className="text-xs text-red-500 hover:text-red-600 font-medium transition-colors cursor-pointer">Clear All</button>
        </div>

        {/* Guest notice */}
        {!isLoggedIn && (
          <div className="bg-forest-50 border border-forest-100 rounded-xl p-4 mb-6 flex items-start gap-3">
            <svg className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-forest-800">Sign up for a better experience</p>
              <p className="text-xs text-gold-400 mt-0.5">Your cart items will be saved. Create an account for easier order management.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3">
            {/* Select All */}
            <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card px-4 py-3 flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={selectedItems.length === items.length} onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 text-forest-700 border-warm-300 rounded focus:ring-forest-500/20" />
                <span className="text-sm font-medium text-forest-700">Select All ({selectedItems.length}/{items.length})</span>
              </label>
            </div>

            {/* Items */}
            {items.map((item) => (
              <div key={item.id} className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-4 sm:p-5">
                <div className="flex gap-3 sm:gap-4">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" checked={selectedItems.includes(item.id)} onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                      className="w-4 h-4 text-forest-700 border-warm-300 rounded focus:ring-forest-500/20 mt-1 flex-shrink-0" />
                    <Link to={`/product/${item.productId}`} className="flex-shrink-0">
                      <img src={item.image || '/images/placeholder.png'} alt={item.name} className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg bg-cream-100" />
                    </Link>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <Link to={`/product/${item.productId}`} className="text-sm sm:text-base font-semibold text-forest-900 hover:text-gold-400 transition-colors line-clamp-1">
                          {item.name}
                        </Link>
                        <p className="text-xs text-warm-500 mt-0.5">{item.manufacturer}</p>
                      </div>
                      <p className="text-base sm:text-lg font-bold text-forest-900 whitespace-nowrap">
                        ${(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>

                    {/* Pill info */}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-forest-50 text-forest-700 rounded font-medium">{item.pillQuantity} pills/box</span>
                      <span className="px-2 py-1 bg-cream-100 text-warm-600 rounded">
                        Total: {item.pillQuantity * item.quantity} pills ({item.quantity} box{item.quantity > 1 ? 'es' : ''})
                      </span>
                      {item.originalPrice > item.price && (
                        <span className="px-2 py-1 bg-red-50 text-red-600 rounded font-medium">
                          {Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}% OFF
                        </span>
                      )}
                    </div>

                    {/* Price + Qty */}
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-xs text-warm-400 line-through mr-2">${item.originalPrice.toLocaleString()}</span>
                        <span className="text-sm font-semibold text-forest-900">${item.price.toLocaleString()}/box</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center border border-warm-300 rounded-md overflow-hidden">
                          <button onClick={() => handleQuantityChange(item.productId, item.quantity - 1, item.pillQuantity)} disabled={item.quantity <= 1}
                            className="w-8 h-8 flex items-center justify-center text-warm-500 hover:bg-cream-100 disabled:opacity-30 transition-colors cursor-pointer">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                          </button>
                          <span className="w-10 h-8 flex items-center justify-center text-sm font-medium text-forest-900 border-x border-warm-300">{item.quantity}</span>
                          <button onClick={() => handleQuantityChange(item.productId, item.quantity + 1, item.pillQuantity)} disabled={item.quantity >= item.stock}
                            className="w-8 h-8 flex items-center justify-center text-warm-500 hover:bg-cream-100 disabled:opacity-30 transition-colors cursor-pointer">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                          </button>
                        </div>
                        <button onClick={() => removeFromCart(item.productId, item.pillQuantity)}
                          className="w-8 h-8 flex items-center justify-center text-warm-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
            {/* Coupon */}
            <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-5">
              <h3 className="text-sm font-semibold text-forest-900 mb-3">Coupon Code</h3>
              {appliedCoupon ? (
                <div className="flex items-center justify-between px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <span className="text-sm font-medium text-green-700">{appliedCoupon} applied</span>
                  <button onClick={removeCoupon} className="text-xs text-green-600 hover:text-green-700 font-medium cursor-pointer">Remove</button>
                </div>
              ) : (
                <div className="space-y-2">
                  <button onClick={handleBestCouponApply} disabled={isProcessing}
                    className="w-full px-4 py-2.5 bg-gold-400 text-forest-900 rounded-lg hover:bg-gold-500 disabled:bg-gold-400 text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-card">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    {isProcessing ? 'Searching...' : 'Auto-Apply Best Coupon'}
                  </button>
                  <div className="flex gap-2">
                    <input type="text" placeholder="Enter code" value={couponCode} onChange={(e) => setCouponCode(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCouponApply()}
                      className="flex-1 px-3 py-2 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-gold-400/20 focus:border-gold-400 outline-none" />
                    <button onClick={handleCouponApply} className="px-3 py-2 bg-warm-100 text-forest-700 rounded-lg hover:bg-warm-200 text-xs font-medium transition-colors cursor-pointer">Apply</button>
                  </div>
                </div>
              )}
            </div>

            {/* Points */}
            {isLoggedIn && (
              <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-forest-900">Reward Points</h3>
                  <span className="text-xs font-bold text-gold-400">{availablePoints.toLocaleString()} pts</span>
                </div>
                <div className="flex gap-2">
                  <input type="number" step="1" min="0" placeholder="Points" value={pointsToUse || ''}
                    onChange={(e) => { const val = parseInt(e.target.value) || 0; setPointsToUse(Math.min(val, availablePoints, Math.floor(selectedSubtotal + selectedShippingFee - discount))); }}
                    className="flex-1 px-3 py-2 bg-cream-50 border border-warm-300 rounded-xl text-sm focus:ring-2 focus:ring-forest-500/20 focus:border-forest-600 outline-none" />
                  <button onClick={() => setPointsToUse(Math.min(availablePoints, Math.floor(selectedSubtotal + selectedShippingFee - discount)))}
                    className="px-3 py-2 bg-warm-100 text-forest-700 rounded-lg hover:bg-warm-200 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap">Use All</button>
                </div>
                {pointsToUse > 0 && <p className="text-xs text-gold-400 mt-2">{pointsToUse.toLocaleString()}P discount applied</p>}
              </div>
            )}

            {/* Summary */}
            <div className="bg-cream-50 rounded-xl border border-warm-200 shadow-card p-5">
              <h3 className="text-sm font-semibold text-forest-900 mb-4">Order Summary</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-warm-500">Subtotal</span><span className="text-forest-900">${selectedSubtotal.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-warm-500">Shipping</span><span className="text-forest-900">{selectedShippingFee === 0 ? 'Free' : `$${selectedShippingFee.toLocaleString()}`}</span></div>
                {(discount + pointsToUse) > 0 && (
                  <div className="flex justify-between"><span className="text-warm-500">Discount</span><span className="text-red-500">-${(discount + pointsToUse).toLocaleString()}</span></div>
                )}
              </div>

              {/* Free shipping progress */}
              {selectedSubtotal < 150 && (
                <div className="mt-3 p-3 bg-forest-50 rounded-xl">
                  <div className="flex justify-between items-center text-xs mb-1.5">
                    <span className="font-medium text-forest-800">Free shipping progress</span>
                    <span className="text-gold-400">${(150 - selectedSubtotal).toLocaleString()} more</span>
                  </div>
                  <div className="w-full bg-forest-100 rounded-full h-1.5">
                    <div className="bg-gold-400 h-1.5 rounded-full transition-all duration-500" style={{ width: `${freeShippingProgress}%` }} />
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-3 mt-3 border-t border-warm-200">
                <span className="text-base font-bold text-forest-900">Total</span>
                <span className="text-xl font-bold text-gold-400">${Math.max(0, selectedTotal).toLocaleString()}</span>
              </div>

              <button onClick={handleCheckout} disabled={selectedItems.length === 0}
                className="w-full mt-4 bg-forest-800 text-cream-100 border border-forest-700/50 py-3 rounded-lg font-semibold text-sm hover:bg-forest-700 disabled:bg-warm-300 disabled:cursor-not-allowed transition-all cursor-pointer shadow-card">
                Checkout ({selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''})
              </button>
            </div>

            <Link to="/shop" className="block w-full text-center py-2.5 text-sm text-warm-500 hover:text-forest-700 font-medium transition-colors">
              Continue Shopping
            </Link>

            {/* Substitution fine print */}
            <SubstitutionNotice className="px-1 pt-1" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
